import { useCallback, useEffect, useState } from "react";
import { toast } from "@mypos/ui";

type StoreLite = { id: string; name: string };

// Wires a topbar dropdown to "I want to operate as a different store".
// Implementation: PATCH the current user's storeId on the server, then
// window.location.reload() so the renderer rehydrates auth + drawer +
// per-store caches against the new store. In Till the main-process
// settings (store.id) are refreshed by the post-reload auth:me handler.
//
// Callers should not invoke switchTo() when a drawer/shift is open — see
// the drawer-open guard in the topbars.
export function useStoreSwitcher(opts: {
  currentStoreId: string | null;
  userId: string | undefined;
}) {
  const [stores, setStores] = useState<StoreLite[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void window.till.api
      .get<StoreLite[]>("/stores")
      .then((rows) => { if (!cancelled) setStores(rows); })
      .catch(() => { /* topbar falls back to the static Pill when the list is empty */ });
    return () => { cancelled = true; };
  }, []);

  const switchTo = useCallback(async (newStoreId: string) => {
    if (!opts.userId || newStoreId === opts.currentStoreId) return;
    setBusy(true);
    try {
      await window.till.api.patch(`/users/${opts.userId}`, { storeId: newStoreId });
      // CRITICAL: Till uses getSetting('store.id') in main-process IPC handlers
      // (drawer:open, sales:create, expense:create, sync agent, etc). The
      // post-reload auth:me would normally refresh it, but if api.me() races
      // or fails the user gets "No store configured" despite a successful
      // PATCH. Writing the setting directly here makes the switch atomic.
      // PWA's tillShim has settings.set as a no-op, so this is safe in both.
      try { await window.till.settings.set("store.id", newStoreId); } catch { /* PWA no-op */ }
      window.location.reload();
    } catch (e) {
      setBusy(false);
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(`Switch store failed: ${msg}`);
    }
  }, [opts.userId, opts.currentStoreId]);

  return { stores, busy, switchTo };
}
